'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { TerminalSquare, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';

export function TerminalTeaser() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl overflow-hidden border border-[var(--color-border-subtle)]"
          style={{
            background: 'linear-gradient(135deg, #0A0E17 0%, #111827 100%)',
          }}
        >
          <div className="grid lg:grid-cols-2 items-stretch">
            <div className="p-8 sm:p-12 flex flex-col justify-center">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-mono font-semibold uppercase tracking-wider w-fit"
                style={{
                  color: '#22D3EE',
                  backgroundColor: 'rgba(34, 211, 238, 0.10)',
                  border: '1px solid rgba(34, 211, 238, 0.25)',
                }}
              >
                <TerminalSquare size={12} />
                New: Terminal mode
              </span>
              <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                An Investor-style terminal,{' '}
                <span style={{ color: '#22D3EE' }}>without the price tag</span>.
              </h2>
              <p className="mt-4 text-base leading-relaxed" style={{ color: '#9CA3AF' }}>
                Real-time quotes, charts with VIX overlay, AI research, market movers, and news all on
                one dense screen — designed for active investors.
              </p>
              <div className="mt-7">
                <Link
                  href="/landing/terminal"
                  className={cn(
                    'inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                    'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]'
                  )}
                >
                  See Terminal in action
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            <div className="p-6 sm:p-8 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}>
              <div
                className="w-full max-w-md rounded-xl overflow-hidden border font-mono"
                style={{ borderColor: '#1F2937', backgroundColor: '#0A0E17' }}
              >
                <div
                  className="flex items-center gap-1.5 px-3 py-2"
                  style={{ backgroundColor: '#111827', borderBottom: '1px solid #1F2937' }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: '#ef4444', opacity: 0.8 }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: '#f59e0b', opacity: 0.8 }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: '#22c55e', opacity: 0.8 }} />
                  <span className="ml-2 text-[10px]" style={{ color: '#6B7280' }}>equitylabs · terminal</span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-base font-bold" style={{ color: '#22D3EE' }}>AAPL</span>
                    <span className="text-base font-bold tabular-nums text-white">$187.43</span>
                    <span className="text-xs tabular-nums" style={{ color: '#10B981' }}>+1.32%</span>
                  </div>
                  <svg viewBox="0 0 240 60" className="w-full h-12" preserveAspectRatio="none">
                    <path
                      d="M0,40 L20,38 L40,42 L60,32 L80,34 L100,28 L120,24 L140,26 L160,18 L180,20 L200,14 L220,12 L240,8"
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <div className="grid grid-cols-3 gap-1.5 text-[9px]">
                    {[
                      { l: 'OPEN', v: '186.20' },
                      { l: 'HIGH', v: '189.42' },
                      { l: 'VOL', v: '52.4M' },
                    ].map((k) => (
                      <div
                        key={k.l}
                        className="rounded px-2 py-1.5"
                        style={{ backgroundColor: '#111827', border: '1px solid #1F2937' }}
                      >
                        <div className="uppercase tracking-wider" style={{ color: '#6B7280' }}>{k.l}</div>
                        <div className="font-bold tabular-nums text-white">{k.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
